import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen, Star, Truck, BadgeCheck, Lock, Heart, BookMarked, X,
} from "lucide-react";
import Header from "@/components/Header";
import ImageCarousel from "@/components/ImageCarousel";
import BookCard from "@/components/BookCard";
import BookDetailDialog from "@/components/BookDetailDialog";
import BuyNowDialog from "@/components/BuyNowDialog";
import CartDrawer from "@/components/CartDrawer";
import CheckoutDialog from "@/components/CheckoutDialog";
import { api } from "@/lib/api";
import { GENRES } from "@/lib/genres";

const TRUST = [
  { icon: Truck, title: "Envíos a todo el país", desc: "Recibe tus libros en la comodidad de tu hogar o en el colegio." },
  { icon: BadgeCheck, title: "Libros originales", desc: "Garantizamos la calidad y autenticidad de cada título." },
  { icon: Lock, title: "Compra segura", desc: "Tus datos y pagos están protegidos." },
  { icon: Heart, title: "Fomentamos la lectura", desc: "Porque un buen libro siempre es una gran idea." },
];

export default function Home() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeGenre, setActiveGenre] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [buyBook, setBuyBook] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyQty, setBuyQty] = useState(1);
  const [directItems, setDirectItems] = useState(null);
  const [shippingFee, setShippingFee] = useState(0);
  const [carousel, setCarousel] = useState({ enabled: false, slides: [] });

  const catalogRef = useRef(null);
  const genresRef = useRef(null);

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data)).finally(() => setLoading(false));
    api.get("/cities").then((r) => setShippingFee(r.data.shipping_fee || 0)).catch(() => {});
    api.get("/carousel").then((r) => setCarousel({ enabled: !!r.data.enabled, slides: r.data.slides || [] })).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return books.filter((b) => {
      const matchSearch = !q || b.title.toLowerCase().includes(q) || b.author.toLowerCase().includes(q);
      const matchGenre = !activeGenre || b.genre === activeGenre;
      return matchSearch && matchGenre;
    });
  }, [books, search, activeGenre]);

  const scrollTo = (target) => {
    if (target === "top") window.scrollTo({ top: 0, behavior: "smooth" });
    if (target === "catalog") catalogRef.current?.scrollIntoView({ behavior: "smooth" });
    if (target === "genres") genresRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const pickGenre = (g) => {
    setActiveGenre((prev) => (prev === g ? null : g));
    setTimeout(() => catalogRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  const handleSlideAction = (slide) => {
    if (slide.link_type === "book" && slide.link_value) {
      const b = books.find((x) => x.id === slide.link_value);
      if (b) openDetail(b);
    } else if (slide.link_type === "category" && slide.link_value) {
      setActiveGenre(slide.link_value);
      setTimeout(() => catalogRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    }
  };

  const openDetail = (book) => {
    setSelectedBook(book);
    setDetailOpen(true);
  };

  const goCheckout = () => {
    setCartOpen(false);
    setDirectItems(null);
    setCheckoutOpen(true);
  };

  const openBuy = (book) => {
    setBuyBook(book);
    setBuyQty(1);
    setBuyOpen(true);
  };

  const confirmBuy = (qty) => {
    if (!buyBook) return;
    setBuyQty(qty);
    setDirectItems([{
      book_id: buyBook.id,
      title: buyBook.title,
      author: buyBook.author,
      price: buyBook.price,
      image_url: buyBook.image_url,
      publisher: buyBook.publisher,
      shipping_fee: buyBook.shipping_fee ?? 0,
      quantity: qty,
    }]);
    setBuyOpen(false);
    setCheckoutOpen(true);
  };

  const backFromCheckout = () => {
    setCheckoutOpen(false);
    if (directItems && buyBook) {
      setBuyQty(directItems[0].quantity);
      setTimeout(() => setBuyOpen(true), 120);
    } else {
      setTimeout(() => setCartOpen(true), 120);
    }
  };

  return (
    <div className="App min-h-screen bg-[var(--lc-bg)]">
      <Header search={search} onSearchChange={setSearch} onOpenCart={() => setCartOpen(true)} onNav={scrollTo} />

      {carousel.enabled && carousel.slides.length > 0 ? (
        <ImageCarousel slides={carousel.slides} onSlideAction={handleSlideAction} />
      ) : (
        /* HERO — banner estático (reproducción fiel de la referencia) */
        <section className="relative w-full" data-testid="hero-banner">
          <img
            src="/hero-banner.png"
            alt="Grandes historias para grandes aprendizajes. Encuentra los libros del plan lector de tu colegio y fomenta el amor por la lectura."
            className="block w-full h-auto select-none"
            draggable="false"
          />
          {/* Botón funcional superpuesto sobre el 'Ver catálogo' de la ilustración */}
          <button
            type="button"
            onClick={() => scrollTo("catalog")}
            aria-label="Ver catálogo"
            data-testid="ver-catalogo-button"
            className="absolute rounded-full outline-none focus:outline-none focus-visible:outline-none appearance-none"
            style={{ left: "13.2%", top: "70%", width: "17%", height: "20%", WebkitTapHighlightColor: "transparent" }}
          />
          <h1 className="sr-only">Grandes historias para grandes aprendizajes</h1>
        </section>
      )}
      {/* GÉNEROS */}
      <section ref={genresRef} className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-8 scroll-mt-24">
        <div className="bg-white rounded-3xl border border-[var(--lc-border)] shadow-[0_2px_12px_rgba(27,42,91,0.05)] p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="flex items-center gap-2.5 font-display font-semibold text-2xl sm:text-3xl text-[var(--lc-ink)]">
              <BookOpen className="w-7 h-7 text-[var(--lc-ink)]" />
              Explora por género
            </h2>
            {activeGenre && (
              <button onClick={() => setActiveGenre(null)} data-testid="clear-genre" className="flex items-center gap-1 text-sm font-semibold text-[var(--lc-primary)]">
                Ver todos los géneros <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-4 sm:gap-6 overflow-x-auto pb-2 sm:grid sm:grid-cols-5 lg:grid-cols-9 sm:overflow-visible">
            {GENRES.map((g) => {
              const isActive = activeGenre === g.name;
              return (
                <button
                  key={g.name}
                  onClick={() => pickGenre(g.name)}
                  data-testid={`genre-${g.name}`}
                  className="group flex flex-col items-center gap-2 min-w-[76px] shrink-0"
                >
                  <span
                    className="grid place-items-center w-16 h-16 sm:w-[74px] sm:h-[74px] rounded-full transition-transform group-hover:-translate-y-1"
                    style={{ background: g.bg, boxShadow: isActive ? `0 0 0 3px ${g.color}` : "none" }}
                  >
                    <img src={g.img} alt={g.name} className="w-10 h-10 sm:w-11 sm:h-11 object-contain" />
                  </span>
                  <span className={`text-xs sm:text-[13px] font-semibold text-center leading-tight ${isActive ? "text-[var(--lc-primary)]" : "text-[var(--lc-ink)]"}`}>
                    {g.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATÁLOGO */}
      <section ref={catalogRef} className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-10 pb-16 scroll-mt-24">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h2 className="flex items-center gap-2.5 font-display font-semibold text-2xl sm:text-3xl text-[var(--lc-ink)]">
            <Star className="w-6 h-6 text-[var(--lc-secondary)] fill-[var(--lc-secondary)]" />
            {activeGenre ? activeGenre : search ? "Resultados" : "Libros destacados"}
          </h2>
          <span className="text-sm text-[var(--lc-slate)] font-medium">
            {loading ? "Cargando…" : `${filtered.length} libro(s)`}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[var(--lc-border)] h-[440px] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[var(--lc-slate)]" data-testid="no-results">
            <BookMarked className="w-14 h-14 mx-auto mb-4 opacity-40" />
            <p className="font-display font-semibold text-xl text-[var(--lc-ink)]">No encontramos libros</p>
            <p>Prueba con otro título, autor o género.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
            {filtered.map((b, i) => (
              <BookCard key={b.id} book={b} index={i} onOpen={openDetail} onBuy={openBuy} />
            ))}
          </div>
        )}
      </section>

      {/* TRUST BAR */}
      <section className="bg-[#EAF4FB] border-t border-[var(--lc-border)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TRUST.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="flex items-start gap-3">
                <Icon className="w-8 h-8 text-[var(--lc-ink)] shrink-0" strokeWidth={1.8} />
                <div>
                  <p className="font-display font-semibold text-[var(--lc-ink)]">{t.title}</p>
                  <p className="text-sm text-[var(--lc-slate)] leading-snug">{t.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="bg-white border-t border-[var(--lc-border)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col items-center gap-3 text-center text-sm text-[var(--lc-slate)]">
          <img
            src="/logo-edibrante.png"
            alt="Edibrante · Leer para crecer · Plan Lector Escolar"
            className="h-12 sm:h-14 w-auto select-none"
            draggable="false"
          />
          <p>© {new Date().getFullYear()} Edibrante · Plan Lector Escolar</p>
        </div>
      </footer>

      <BookDetailDialog
        book={selectedBook}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onBuy={(b) => { setDetailOpen(false); openBuy(b); }}
      />
      <BuyNowDialog
        book={buyBook}
        open={buyOpen}
        onOpenChange={setBuyOpen}
        onConfirm={confirmBuy}
        shippingFee={shippingFee}
        initialQty={buyQty}
      />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} onCheckout={goCheckout} shippingFee={shippingFee} />
      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={(v) => { setCheckoutOpen(v); if (!v) setDirectItems(null); }}
        directItems={directItems}
        shippingFee={shippingFee}
        onBack={backFromCheckout}
      />
    </div>
  );
}
